import React from "react";
import { Box } from "@material-ui/core";
import "./styles.scss";
import clsx from "clsx";
import { PickerType, usePrevious } from "halifax";
import {
  getConfigTenant,
  isCorpTenant,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  useIsTravelApp,
} from "@capone/common";
import * as textConstants from "./textConstants";
import { MobileCalendarPickerConnectorProps } from "./container";
import {
  MonthAndDatePicker,
  MonthAndDatePickerType,
  PackagesSearchButton,
} from "../../../PackagesSearchControl/components";
import { MobileSelectedDatesDisplay } from "../MobileSelectedDatesDisplay";
import dayjs from "dayjs";

export interface IMobileCalendarPickerProps
  extends MobileCalendarPickerConnectorProps {
  departure: Date | null;
  returnDate: Date | null;
  focusedMonthIndex: number;
  onComplete?: () => void;
  minAllowedDate?: Date;
  maxAllowedDate?: Date;
  onSelectDates?: (departure: Date | null, returnDate: Date | null) => void;
}

export const MobileCalendarPicker = (props: IMobileCalendarPickerProps) => {
  const {
    readyToSearch,
    focusedMonthIndex,
    onComplete,
    setDepartureDate,
    setReturnDate,
    departure,
    returnDate,
    minAllowedDate,
    maxAllowedDate,
    onSelectDates,
  } = props;

  const { isTravelApp } = useIsTravelApp();
  const isCorporate = isCorpTenant(getConfigTenant());

  const incrementPackagesFunnelSearchesCounter = () => {
    const PACKAGES_FUNNEL_SEARCHES = "packages-funnel-searches";
    const searches = parseInt(
      sessionStorage.getItem(PACKAGES_FUNNEL_SEARCHES) || "0"
    );
    sessionStorage.setItem(PACKAGES_FUNNEL_SEARCHES, String(searches + 1));
  };

  const previousDeparture = usePrevious(departure);
  const previousReturn = usePrevious(returnDate);

  React.useEffect(() => {
    travelAppInterface.sendEvent({
      name: TravelAppInterfaceEventName.CALENDAR_LOADED,
      funnel: TravelAppInterfaceFunnelType.PACKAGES,
    });
  }, []);

  React.useEffect(() => {
    if (!(departure && returnDate) && previousDeparture && previousReturn) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATES_RESET,
        funnel: TravelAppInterfaceFunnelType.PACKAGES,
      });
    }
  }, [departure, returnDate]);

  React.useEffect(() => {
    if (departure) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATE_PICKED,
        funnel: TravelAppInterfaceFunnelType.PACKAGES,
        data: {
          startDate: dayjs(departure).format("YYYY-MM-DD"),
        },
      });
    }
  }, [departure]);

  React.useEffect(() => {
    if (returnDate) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATE_PICKED,
        funnel: TravelAppInterfaceFunnelType.PACKAGES,
        data: {
          endDate: dayjs(returnDate).format("YYYY-MM-DD"),
        },
      });
    }
  }, [returnDate]);

  React.useEffect(() => {
    if (departure && returnDate) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATES_FINALIZED,
        funnel: TravelAppInterfaceFunnelType.PACKAGES,
      });
    }
  }, [departure, returnDate]);

  return (
    <Box className="mobile-packages-calendar-picker-root">
      <MonthAndDatePicker
        className="b2b"
        pickerType={PickerType.RANGE}
        viewType={MonthAndDatePickerType.Column}
        focusedMonthIndex={focusedMonthIndex}
        setStartDate={setDepartureDate}
        setEndDate={setReturnDate}
        startDate={departure}
        endDate={returnDate}
        minAllowedDate={minAllowedDate}
        maxAllowedDate={maxAllowedDate}
        header={
          <MobileSelectedDatesDisplay
            returnDate={returnDate}
            departureDate={departure}
          />
        }
      />
      {readyToSearch && !isTravelApp && (
        <Box className="mobile-packages-search-button-container">
          <PackagesSearchButton
            className={clsx("mobile-calendar-picker-search-button", "b2b")}
            message={
              isCorporate
                ? textConstants.CORPORATE_SEARCH_BUTTON_TEXT
                : textConstants.SEARCH_BUTTON_TEXT
            }
            onClick={() => {
              onComplete && onComplete();
              onSelectDates?.(departure, returnDate);
              incrementPackagesFunnelSearchesCounter();
            }}
          />
        </Box>
      )}
    </Box>
  );
};
